import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { APPOINTMENT_URL } from './appointment.js';
import Wordmark from './Wordmark';

const NAV = [
  { to: '/telemedicine', label: 'Telemedicine' },
  { to: '/wellness', label: 'Crew Wellness' },
  { to: '/evidence', label: 'Evidence & Security' },
  { to: '/academy', label: 'Academy' },
];

const COMPANY_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/about#press', label: 'Press' },
  { to: '/pricing', label: 'Pricing' },
];

const FOOTER_PRODUCTS = [
  { to: '/peme', label: 'PEME overview' },
  { to: '/peme/review', label: 'PEME Review' },
  { to: '/peme/examinations', label: 'PEME Examination' },
  { to: '/telemedicine', label: 'Telemedicine' },
  { to: '/wellness', label: 'Crew Wellness' },
];

const FOOTER_COMPANY = [
  { to: '/about', label: 'About' },
  { to: '/about#press', label: 'Press' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/blog', label: 'Blog' },
  { to: '/academy', label: 'SeaScope Academy' },
];

// Founder, 22 Sep 2026: seafarers must be able to find the medical portal, and
// must not land in the wellness app by mistake. Both are named for what they do.
const FOOTER_CREW = [
  {
    to: '/crew-portal',
    label: 'Crew medical portal',
    note: 'How to use your personal Form A link',
  },
  {
    href: 'https://app.seascope.tech/crew/join',
    label: 'Crew Wellness app',
    note: 'Readings between medicals',
  },
];

const FOOTER_LEGAL = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms of Service' },
  { to: '/evidence#security', label: 'Security' },
];

const navLinkClass =
  'whitespace-nowrap text-[15px] font-medium text-ink-2 transition-colors duration-150 ease-out hover:text-teal-strong';

/**
 * White nav and footer (clinical daylight). Every route is light after Stage 4.
 */
export default function SiteLayout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [pemeOpen, setPemeOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    setMobileMenuOpen(false);
    setCompanyOpen(false);
    setPemeOpen(false);
    /* eslint-enable react-hooks/set-state-in-effect */
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
        else window.scrollTo(0, 0);
      }, 50);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen bg-white text-ink-2 antialiased">
      <nav
        className="sticky top-0 z-50 h-20 border-b border-line bg-white"
        aria-label="Main navigation"
        onBlurCapture={(event) => {
          if (mobileMenuOpen && !event.currentTarget.contains(event.relatedTarget)) {
            setMobileMenuOpen(false);
            setPemeOpen(false);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && mobileMenuOpen) {
            setMobileMenuOpen(false);
            setPemeOpen(false);
            event.currentTarget.querySelector('button[aria-label="Toggle menu"]')?.focus();
          }
        }}
      >
        <div className="container-wide flex h-full items-center justify-between">
          <Link to="/" aria-label="SeaScope home">
            <Wordmark />
          </Link>

          <div className="hidden items-center gap-6 xl:flex">
            <div
              className="relative"
              onKeyDown={(event) => {
                if (event.key === 'Escape' && pemeOpen) {
                  setPemeOpen(false);
                  event.currentTarget.querySelector('button')?.focus();
                }
              }}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setPemeOpen(false);
              }}
            >
              <button
                type="button"
                className={`${navLinkClass} inline-flex items-center gap-1`}
                aria-expanded={pemeOpen}
                aria-controls="peme-menu-desktop"
                onClick={() => setPemeOpen((open) => !open)}
              >
                PEME <ChevronDown className="h-4 w-4" aria-hidden="true" />
              </button>
              {pemeOpen && (
                <div id="peme-menu-desktop" className="absolute left-0 top-full z-50 min-w-[13rem] rounded-card border border-line bg-white py-2 shadow-soft">
                  <Link to="/peme" className="block px-4 py-2 text-[15px] hover:bg-bg-alt">Overview</Link>
                  <Link to="/peme/review" className="block px-4 py-2 text-[15px] hover:bg-bg-alt">Review</Link>
                  <Link to="/peme/examinations" className="block px-4 py-2 text-[15px] hover:bg-bg-alt">Examinations</Link>
                </div>
              )}
            </div>
            {NAV.map((item) => (
              <Link key={item.to} to={item.to} className={navLinkClass}>
                {item.label}
              </Link>
            ))}
            <div
              className="relative"
              onKeyDown={(event) => {
                if (event.key === 'Escape' && companyOpen) {
                  setCompanyOpen(false);
                  event.currentTarget.querySelector('button')?.focus();
                }
              }}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setCompanyOpen(false);
                }
              }}
            >
              <button
                type="button"
                className={`${navLinkClass} inline-flex items-center gap-1`}
                aria-expanded={companyOpen}
                aria-controls="company-menu"
                onClick={() => setCompanyOpen((open) => !open)}
              >
                Company
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              </button>
              {companyOpen && (
                <div
                  id="company-menu"
                  className="absolute left-0 top-full z-50 min-w-[12rem] rounded-card border border-line bg-white py-2 shadow-soft"
                >
                  {COMPANY_LINKS.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="block px-4 py-2 text-[15px] text-ink-2 hover:bg-bg-alt hover:text-teal-strong"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Two primary actions, same weight, same green: the commercial one
              first, the seafarer's to its right (founder, 23 Sep 2026). */}
          <div className="hidden shrink-0 items-center gap-3 xl:flex">
            <a
              href={APPOINTMENT_URL}
              target="_blank"
              rel="noopener"
              className="btn-primary whitespace-nowrap px-5 text-sm"
            >
              Book a meeting
            </a>
            <Link to="/crew-portal" className="btn-primary whitespace-nowrap px-5 text-sm">
              Crew portal
            </Link>
          </div>

          <button
            type="button"
            onClick={() => {
              setMobileMenuOpen(!mobileMenuOpen);
              setPemeOpen(false);
            }}
            className="text-ink xl:hidden"
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-site-menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div id="mobile-site-menu" className="max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain border-b border-line bg-white xl:hidden">
            <div className="container-wide space-y-1 py-4">
              <div
                onKeyDown={(event) => {
                  if (event.key === 'Escape' && pemeOpen) {
                    event.stopPropagation();
                    setPemeOpen(false);
                    event.currentTarget.querySelector('button')?.focus();
                  }
                }}
              >
                <button
                  type="button"
                  className="flex w-full items-center justify-between py-3 text-ink-2"
                  aria-expanded={pemeOpen}
                  aria-controls="peme-menu-mobile"
                  onClick={() => setPemeOpen((open) => !open)}
                >
                  PEME <ChevronDown className="h-4 w-4" aria-hidden="true" />
                </button>
                {pemeOpen && (
                  <div id="peme-menu-mobile" className="border-l border-line pl-4">
                    <Link to="/peme" className="block py-3 text-ink-2">Overview</Link>
                    <Link to="/peme/review" className="block py-3 text-ink-2">Review</Link>
                    <Link to="/peme/examinations" className="block py-3 text-ink-2">Examinations</Link>
                  </div>
                )}
              </div>
              {NAV.map((item) => (
                <Link key={item.to} to={item.to} className="block py-3 text-ink-2">
                  {item.label}
                </Link>
              ))}
              <p className="label-caps pt-3">Company</p>
              {COMPANY_LINKS.map((item) => (
                <Link key={item.to} to={item.to} className="block py-3 pl-3 text-ink-2">
                  {item.label}
                </Link>
              ))}
              <a
                href={APPOINTMENT_URL}
                target="_blank"
                rel="noopener"
                className="btn-primary mt-3 w-full"
                onClick={() => setMobileMenuOpen(false)}
              >
                Book a meeting
              </a>
              <Link
                to="/crew-portal"
                className="btn-primary mt-3 w-full"
                onClick={() => setMobileMenuOpen(false)}
              >
                Crew portal
              </Link>
            </div>
          </div>
        )}
      </nav>

      <main>{children}</main>

      <footer className="border-t border-line bg-white py-16 lg:py-24">
        <div className="container-wide grid gap-12 md:grid-cols-4">
          <div>
            <Link to="/" className="inline-flex">
              <img
                src="/rebuild/brand/seascope-logo-lockup.png"
                alt="SeaScope by RemoteAid Medical Solutions"
                className="h-[110px] w-auto"
                width="131"
                height="110"
              />
            </Link>
            <p className="mt-6 text-sm leading-relaxed text-ink-3">
              SeaScope by RemoteAid Medical Solutions
              <br />
              Clinical services for fleet operators.
            </p>
          </div>
          <div>
            <p className="label-caps mb-6 text-xs">Products</p>
            <ul className="space-y-4">
              {FOOTER_PRODUCTS.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-[15px] text-ink-2 transition-colors duration-150 hover:text-teal-strong"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="label-caps mb-6 mt-10 text-xs">For seafarers</p>
            <ul className="space-y-4">
              {FOOTER_CREW.map((item) => (
                <li key={item.to || item.href}>
                  {item.to ? (
                    <Link to={item.to} className="text-[15px] text-ink-2 transition-colors duration-150 hover:text-teal-strong">
                      {item.label}
                    </Link>
                  ) : (
                    <a href={item.href} target="_blank" rel="noopener noreferrer" className="text-[15px] text-ink-2 transition-colors duration-150 hover:text-teal-strong">
                      {item.label}
                    </a>
                  )}
                  <span className="mt-0.5 block text-[13px] text-ink-3">{item.note}</span>
                </li>
              ))}
              <li>
                <Link
                  to="/peme#crew"
                  className="text-[15px] text-ink-2 transition-colors duration-150 hover:text-teal-strong"
                >
                  How to fill in Form A
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <p className="label-caps mb-6 text-xs">Company</p>
            <ul className="space-y-4">
              {FOOTER_COMPANY.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-[15px] text-ink-2 transition-colors duration-150 hover:text-teal-strong"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="mailto:hello@seascope.tech"
                  className="text-[15px] text-ink-2 transition-colors duration-150 hover:text-teal-strong"
                >
                  hello@seascope.tech
                </a>
              </li>
            </ul>
          </div>
          <div>
            <p className="label-caps mb-6 text-xs">Legal</p>
            <ul className="space-y-4">
              {FOOTER_LEGAL.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-[15px] text-ink-2 transition-colors duration-150 hover:text-teal-strong"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
